import { Button, Navbar, Spinner, Tooltip } from 'flowbite-react';
import { get } from 'lodash';
import moment from 'moment';

import React, { useEffect } from 'react';
import { Img } from 'react-image';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createSessionLog, startSession } from 'src/apis/session';
import { useToast } from 'src/context/ToastProvider';
import { useUserContext } from 'src/context/UserProvider';
import { useGetSessionByIdQuery } from 'src/hooks/session.hooks';
import { interviewScreenInstructions } from 'src/utils/interviewScreenInstructions';
import { SessionJoineeStatus, SessionStatus, SessionType, UISession } from 'types';
import { ONWARD_DASHBOARD_BASE_URL } from 'src/config/env.config';
import { isUserAlumniConnect, sendMessageToParent } from 'shared-utils';
import { useThemeContext } from 'src/context/ThemeProvider';
type Props = {
	session: UISession;
	isHost: boolean;
};
const SessionNotStartedScreen = ({ session, isHost }: Props) => {
	const queryClient = useQueryClient();
	const userData = useUserContext();
	const navigate = useNavigate();
	const toast = useToast();
	const { themeMode } = useThemeContext();

	const [searchParams] = useSearchParams();
	const token = userData?.isAlumniConnectLoggedIn ? '' : searchParams.get('token') || '';
	const sessionInfo = useGetSessionByIdQuery(session._id, token);
	const startSessionFn = useMutation({
		mutationKey: 'startSession',
		mutationFn: startSession,
		onSuccess() {
			queryClient.refetchQueries(['getSessionById', session._id]);
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				type: 'error',
				title: errorMessage,
			});
		},
	});

	const addUserSessionLog = useMutation({
		mutationKey: 'createSessionLog',
		mutationFn: createSessionLog,
	});

	const leaveSession = () => {
		if (session && (session?.levelUpParticipant?._id || session?.participant?._id)) {
			const payload = {
				participant:
					session.type === SessionType.MENTOR_SESSION ? session?.levelUpParticipant?._id : session?.participant?._id,
				status: SessionJoineeStatus.LEFT,
				sessionId: session.dyteMeetingId,
			};
			addUserSessionLog.mutate(payload);
			if (session.type === SessionType.MENTOR_SESSION) {
				navigate('/allMentors');
			}
		}
	};

	useEffect(() => {
		let checkingIfSessionHasStarted: any = '';
		if (session.sessionStatus !== SessionStatus.STARTED) {
			checkingIfSessionHasStarted = setInterval(() => {
				sessionInfo.refetch();
			}, 5000);
		} else {
			clearInterval(checkingIfSessionHasStarted);
		}
		return () => {
			clearInterval(checkingIfSessionHasStarted);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [session]);

	const sessionDateTime = moment(session.startTime);
	const date = sessionDateTime.format('DD MMM YYYY');
	const time = sessionDateTime.format('hh:mm a');

	const waitingTime = moment(session.startTime).add(10, 'minutes').format('hh:mm a');

	const disableStarting = moment().isBefore(sessionDateTime.subtract(2, 'minutes'));
	const showNavbar = isHost;
	return (
		<div className="h-screen w-screen dark:text-white">
			{showNavbar && (
				<Navbar
					className={`w-full rounded-none !px-[45px] sm:!px-[10px] bg-white dark:bg-[#18222D]`}
					fluid={true}
					rounded={true}
				>
					<div className="flex items-center gap-8 ">
						<div
							onClick={() => {
								leaveSession();
								// navigate('/dashboard');
								navigate('/my-applications');
							}}
							className="flex items-center"
						>
							<Img
								loading="lazy"
								src={
									themeMode === 'dark'
										? 'https://masai-website-images.s3.ap-south-1.amazonaws.com/level_Up_By_Masai_Icon_Dark_a5f53496b9.svg'
										: 'https://masai-website-images.s3.ap-south-1.amazonaws.com/level_Up_By_Masai_Icon_Dark_a5f53496b9.svg'
								}
								width="110"
								height="32px"
								alt="LevelUp Logo"
							/>
						</div>
					</div>
				</Navbar>
			)}
			<div
				className={`${showNavbar ? 'my-60' : 'py-60'
					} px-2  flex flex-col items-center justify-center gap-2 dark:text-white`}
			>
				<p className=" text-2xl font-bold">
					Session starts at <span className="text-blue-600 dark:text-blue-400">{time}</span> on{' '}
					<span className="text-blue-600 dark:text-blue-400">{date}</span> and is for about{' '}
					<span className="text-blue-600 dark:text-blue-400">{session.duration} minutes</span>.
				</p>
				{isHost ? (
					<Tooltip placement="bottom" content="Session can only started upto 2 minutes before the start time.">
						<Button
							color="purplePlaced"
							onClick={() => {
								startSessionFn.mutate(session._id);
							}}
							disabled={disableStarting || startSessionFn.isLoading}
						>
							{startSessionFn.isLoading ? <Spinner size="sm" light={true} /> : 'Start Session'}
						</Button>
					</Tooltip>
				) : (
					<div className="flex flex-col items-center">
						<p className=" text-base">The session will be started by the host at the mentioned time.</p>
						<br />

						<p className="text-lg">
							We request you to wait till{' '}
							<span className="font-bold text-blue-600 dark:text-blue-400">{waitingTime}</span> for our host to join
							before dropping off.
						</p>
						<br />
						<div>
							<p>Meanwhile, please ensure the following:</p>
							{interviewScreenInstructions.map(i => (
								<p key={i}>{i}</p>
							))}
						</div>
						<Button
							color="purplePlaced"
							className="mt-6"
							onClick={() => {
								leaveSession();
								// open onward dashboard
								console.log('3. sending message to parent');
								sendMessageToParent();
							}}
						>
							Ok, Got it
						</Button>
					</div>
				)}
			</div>
		</div>
	);
};

export default SessionNotStartedScreen;
